import _jsx from "@babel/runtime/helpers/jsx";
var _InfoIcon;
/**
 *  This "Pricing" Component includes the front end logic and display for inventory pricing
 *
 **/
import PropTypes from 'prop-types';
import React from 'react';
import clsx from 'clsx';
import { Text, InfoIcon } from 'reaxl';
import { formatCurrency, formatNumber } from '@atc/string-fns';

/**
 * Get the various pricing values based on uiContext
 * @param {Object} data (pricing data)
 * @param {String} uiContext
 *
 * @returns {Object} pricing variables
 */
const getPricingVariables = (_ref, uiContext) => {
  let {
    msrp,
    salePrice,
    noPriceLabel,
    incentive
  } = _ref;
  let firstPrice;
  let secondPrice;
  let noPrice;
  const salePriceValue = incentive || salePrice;
  const salePriceOnly = salePriceValue && !msrp;
  const msrpOnly = msrp && msrp !== 0 && !salePriceValue;
  const saleAndMSRPNotEqual = salePriceValue && msrp && salePriceValue !== msrp;
  const saleAndMSRPEqual = salePriceValue && msrp && salePriceValue === msrp;
  const priceDifference = salePriceValue && msrp && msrp - salePriceValue;
  const hasDeal = priceDifference > 0;
  const showMSRPLabelOnly = msrpOnly || saleAndMSRPEqual;
  const isDefault = uiContext === 'default';
  const isWithDeal = uiContext === 'withDeal';
  let priceClassName = clsx({
    'text-size-300 text-link link-undecorated text-gray-base text-bold': isDefault,
    'text-size-500 text-ultra-bold': ['details', 'listing', 'snapshot'].includes(uiContext)
  });
  if (salePriceOnly) {
    firstPrice = formatNumber({
      value: salePriceValue
    });
  } else if (showMSRPLabelOnly) {
    firstPrice = formatNumber({
      value: msrp
    });
    secondPrice = formatCurrency(msrp);
  } else if (saleAndMSRPNotEqual) {
    firstPrice = formatNumber({
      value: salePriceValue
    });
    secondPrice = formatCurrency(msrp);
  } else {
    firstPrice = noPriceLabel;
    priceClassName = 'text-size-300 text-bold';
    noPrice = true;
  }
  secondPrice = secondPrice && ['listing', 'details', 'withDeal'].includes(uiContext) ? /*#__PURE__*/React.createElement(React.Fragment, null, 'MSRP ', !showMSRPLabelOnly && /*#__PURE__*/_jsx(Text, {
    weight: isWithDeal ? 'bold' : 'normal'
  }, void 0, secondPrice)) : null;
  return {
    firstPrice,
    secondPrice,
    priceClassName,
    priceDifference: hasDeal && formatCurrency(priceDifference),
    noPrice
  };
};
function Pricing(_ref2) {
  let {
    className,
    data = {},
    isPreorder = false,
    // TODO convert to uiContext
    onPricingDetailClick = () => {},
    orientation,
    uiContext = 'listing',
    ...rest
  } = _ref2;
  const showDeal = uiContext === 'withDeal';
  const {
    firstPrice,
    secondPrice,
    priceClassName,
    priceDifference,
    noPrice
  } = getPricingVariables(data, uiContext);
  const rootClasses = clsx(className, {
    'price-version-2': true,
    'align-items-center': showDeal && priceDifference,
    'align-items-end': !showDeal || !priceDifference,
    'justify-content-end': isPreorder
  }, 'display-flex flex-wrap');
  return /*#__PURE__*/React.createElement("div", Object.assign({
    "data-cmp": "pricing",
    className: rootClasses
  }, rest), /*#__PURE__*/_jsx("div", {
    className: clsx(priceClassName, {
      'first-price text-gray-base': !showDeal && !noPrice,
      'first-price first-price-lg text-gray-base text-ultra-bold text-size-700': showDeal && !noPrice
    }),
    "data-cmp": "firstPrice"
  }, void 0, firstPrice), secondPrice && !isPreorder && /*#__PURE__*/_jsx(Text, {
    componentClass: "div",
    color: "subdued-lighter",
    size: 200,
    className: clsx('padding-left-2', {
      'second-price': !showDeal,
      'second-price-lg': showDeal && !priceDifference
    })
  }, void 0, !showDeal ? secondPrice : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/_jsx(Text, {
    className: "display-block",
    size: priceDifference ? 100 : undefined,
    color: "subdued-lightest",
    weight: "normal"
  }, void 0, secondPrice), !!priceDifference && /*#__PURE__*/_jsx(Text, {
    className: "display-block",
    color: "success",
    size: 200,
    weight: "light"
  }, void 0, `Save ${priceDifference}`))), isPreorder && !showDeal && /*#__PURE__*/_jsx(Text, {
    size: 200,
    color: "subdued-lightest",
    weight: "normal",
    className: "display-block padding-bottom-3 second-price"
  }, void 0, _InfoIcon || (_InfoIcon = /*#__PURE__*/_jsx(InfoIcon, {
    popoverId: "msrp-more-info-popover",
    placement: "top",
    infoText: "MSRP excludes tax, title, license, delivery, processing & handling fees and optional equipment. Contact dealer for details."
  })), "Starting MSRP", ' ', /*#__PURE__*/_jsx("span", {
    className: clsx({
      'display-block': orientation === 'landscape'
    })
  }, void 0, "before options")));
}
export default Pricing;